/**
 * Safely divides two numbers.
 * If the divisor is 0, it returns 0 to avoid division by zero error.
 *
 * @param dividend The dividend
 * @param divisor The divisor
 * @returns The result of the division or 0 if the divisor is 0
 */
export function safeDivide(dividend: number, divisor: number): number {
  if (divisor === 0) {
    return 0;
  }

  return dividend / divisor;
}

/**
 * Safely converts a value to a number.
 * If the value is already a number, it will be returned as is.
 * If the value is not a number, it will return Infinity.
 *
 * @param val The value to convert
 * @returns The converted number or Infinity if the value is not a number
 */
export function safeTypeNumber(val: unknown): number {
  // Implementation goes here
  return typeof val === 'number' ? val : Infinity;
}

/**
 * Column map type: key is column name (e.g. 'uw_data.GEP_AMOUNT'), value is row index.
 */
export type DashboardColumnMap = Record<string, number>;

/**
 * Normalizes dashboard data rows to a target column map so that current and comparison
 * datasets align. Uses source column indices to read values and target column indices
 * to write; fills 0 for any target column that does not exist in the source.
 *
 * @param data Dashboard data (array of row arrays)
 * @param sourceColumnMap Column map that the current data rows use
 * @param targetColumnMap Column map to align to (e.g. the "exposure" / fuller map)
 * @returns New array of normalized rows (aligned to targetColumnMap, missing cells filled with 0)
 */
export function normalizeDashboardDataToColumnMap(
  data: unknown[][],
  sourceColumnMap: DashboardColumnMap,
  targetColumnMap: DashboardColumnMap
): unknown[][] {
  const maxTargetIndex = Math.max(...Object.values(targetColumnMap), -1);

  return data.map((row) => {
    const newRow: unknown[] = new Array(maxTargetIndex + 1).fill(0);

    for (const [key, targetIndex] of Object.entries(targetColumnMap)) {
      const sourceIndex = sourceColumnMap[key];
      const value =
        sourceIndex !== undefined && row[sourceIndex] !== undefined && row[sourceIndex] !== null
          ? row[sourceIndex]
          : 0;
      newRow[targetIndex] = value;
    }

    return newRow;
  });
}
